import { useEffect } from 'react';

import { useFreshRef } from './useFreshRef';

interface KeyHandlers {
  [key: string]: (event: KeyboardEvent) => void;
}

interface KeyHandlersOptions {
  disabled?: boolean;
  capture?: boolean;
}

/**
 * Hook that handles keyboard events based on provided handlers
 * @param handlers Object mapping key names to handler functions
 * @param options Configuration options for the hook
 */
export const useKeyHandlers = (handlers: KeyHandlers, options?: KeyHandlersOptions): void => {
  const handlersRef = useFreshRef<KeyHandlers>(handlers);

  const enabled = !options?.disabled;
  const capture = options?.capture;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    /**
     * Element's 'keydown' event handler
     */
    const handleKeyDown = (event: KeyboardEvent): void => {
      const handler = handlersRef.current[event.key];
      if (handler) {
        event.preventDefault();
        event.stopPropagation();

        handler(event);
      }
    };

    window.addEventListener('keydown', handleKeyDown, capture);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, capture);
    };
  }, [enabled, handlersRef, capture]);
};
